/**
 * expr 的 **helper 库** —— 源码里能调用的全部纯函数。
 *
 * ## 两条规矩
 *
 * ① **纯、且确定**。同一份文档两次求值必须给出同样的结果，所以
 *    `now()` / `random()` / `fetch` 这类一律不进。要真实时间就由
 *    宿主通过 `api.data` 注入。
 *
 * ② **不抛异常**。helper 的输入是人手输的、上游算出来的，随时可能是
 *    垃圾。`num('abc')` 返回 `null` 而不是抛 —— 抛出去会变成节点上的
 *    一条错误诊断，而「这格填错了」本来只需要一个空值。
 *    ⚠️ 例外见 `get`：路径写法错误是**作者**的错，要报出来。
 *
 * 加 helper 的检查清单：纯吗？确定吗？垃圾输入给得了一个合理的值吗？
 */
import { COMPARE_OPS, compare, isEmpty, toNumber, valueAt } from '../listOps'
import type { CompareOp } from '../listOps'
import { parseTime } from '../timeText'

/** 空值判定复用内核那一份，避免两处语义漂移 */
export { isEmpty }

/** 数值转换：转不出来返回 `null`（跟 `toNumber` 一致） */
function num(value: unknown): number | null {
  return toNumber(value)
}

/**
 * 字符串转换。
 * ⚠️ `null` / `undefined` → 空串（不是 `"null"`）——
 * 拼模板时出现字面的 "undefined" 是最常见也最烦人的一类错。
 */
function str(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map((item) => str(item)).join(', ')
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

/**
 * 布尔转换。
 *
 * ⚠️ **比 JS 的 `Boolean()` 严格**，这是有意的：
 * `Boolean('false')` 在 JS 里是 **true**（非空字符串），
 * 而模板里的值经常是从**文本控件**来的 —— 上游写着 `false`，
 * 判断却走了「真」那条路，而且不报错，只是结果不对。
 *
 * 规则（按顺序）：
 *   ① 数组 → 有没有元素
 *   ② 字符串 → 能当数字看就用数字的真假（`"0"` / `"00"` 都是假）；
 *      否则认这几个假词（大小写不敏感）：`false` / `no` / `off` / `""`
 *   ③ 其余按 JS 的真假
 */
function bool(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') {
    const text = value.trim()
    if (text === '') return false
    const n = Number(text)
    if (Number.isFinite(n)) return n !== 0
    return !FALSE_WORDS.has(text.toLowerCase())
  }
  return Boolean(value)
}

/**
 * 认作「假」的字符串词。
 *
 * ⚠️ 只收**明确的否定词**，不收 `null` / `undefined` 这类字面文本 ——
 * 那些更像「有人把变量名当字符串填进去了」，当成假会掩盖错误。
 */
const FALSE_WORDS = new Set(['false', 'no', 'off', 'n'])

function len(value: unknown): number {
  if (typeof value === 'string') return value.length
  if (Array.isArray(value)) return value.length
  return 0
}

function pad(value: unknown, width: unknown, fill: unknown = '0'): string {
  const text = str(value)
  const target = Math.max(0, Math.trunc(num(width) ?? 0))
  const padText = str(fill) || '0'
  if (text.length >= target) return text
  /* 补位默认补在**左侧** —— 补位几乎总是补编号：000123 */
  const need = target - text.length
  const repeat = Math.ceil(need / padText.length)
  return padText.repeat(repeat).slice(0, need) + text
}

function fixed(value: unknown, digits: unknown): string {
  const n = num(value)
  if (n === null) return ''
  const d = Math.max(0, Math.min(20, Math.trunc(num(digits) ?? 0)))
  return n.toFixed(d)
}

function join(list: unknown, separator: unknown = ', '): string {
  if (!Array.isArray(list)) return ''
  return list.map((item) => str(item)).join(str(separator))
}

function contains(haystack: unknown, needle: unknown): boolean {
  if (typeof haystack === 'string') return haystack.includes(str(needle))
  if (Array.isArray(haystack)) {
    /* 元素按字符串比 —— 控件里拿到的 `2` 和数组里的 `2` 要能对上 */
    const want = str(needle)
    return haystack.some((item) => str(item) === want)
  }
  return false
}

function slice(value: unknown, start: unknown, end: unknown = undefined): unknown {
  const from = Math.trunc(num(start) ?? 0)
  if (typeof value === 'string') {
    return end === undefined ? value.slice(from) : value.slice(from, Math.trunc(num(end) ?? 0))
  }
  if (Array.isArray(value)) {
    return end === undefined ? value.slice(from) : value.slice(from, Math.trunc(num(end) ?? 0))
  }
  return value
}

function round(value: unknown, digits: unknown = 0): number | null {
  const n = num(value)
  if (n === null) return null
  const d = Math.max(0, Math.min(20, Math.trunc(num(digits) ?? 0)))
  const factor = 10 ** d
  /* 先乘再除，避免 `1.005` 那种浮点尾巴 */
  return Math.round((n + Number.EPSILON) * factor) / factor
}

/**
 * 转成列表：本来就是列表就原样，单个值包成一项，空值给空列表。
 *
 * ⚠️ 为什么要有这个 helper：**expr 里写不出 `Array.isArray(x)`** ——
 * 被调者必须是 `api.x` / `helpers.x`（沙箱规则）。这不是限制过分，
 * 而是那条规则让「能调用的函数集合」在编译期就穷尽了；
 * 需要什么就把它变成 helper。
 */
function toList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined) return []
  return [value]
}

/**
 * 一次比较：`compare(a, 'gt', b)`。
 *
 * 包的是 `listOps.compare`（契约里那 10 种操作符的**唯一实现**）——
 * 这样节点上的枚举、过滤、比较三条路走的是同一套语义，
 * 不会出现「filter 里 `gt` 和 compare 里 `gt` 不一样」。
 *
 * ⚠️ 未知操作符**抛错**（和 `filterBy` 一致）：拼错了不该静默给 false。
 */
function compareWith(a: unknown, op: unknown, b: unknown): boolean {
  const operator = str(op) as CompareOp
  if (!COMPARE_OPS.includes(operator)) {
    throw new Error(`compare 不认识操作符「${str(op)}」`)
  }
  return compare(a, operator, b)
}

/** 夹到区间里（`min` 传 null 表示不限） */
function clamp(value: unknown, low: unknown, high: unknown): number | null {
  const n = num(value)
  if (n === null) return null
  const lo = num(low)
  const hi = num(high)
  let out = n
  if (lo !== null) out = Math.max(lo, out)
  if (hi !== null) out = Math.min(hi, out)
  return out
}

/**
 * 取模。
 *
 * ⚠️ 和 `%` 运算符不同：结果**跟随除数**的符号（`mod(-1, 3)` 给 `2`）。
 * 按份数轮转颜色时，负数下标是会很自然出现的，而 JS 的 `%`
 * 会给负的结果。除数为 0 给 `null`（和 `%` 一致）。
 */
function mod(value: unknown, divisor: unknown): number | null {
  const a = num(value)
  const b = num(divisor)
  if (a === null || b === null || b === 0) return null
  return ((a % b) + b) % b
}

/** 数值包装：转不出来返回 `null`，不用 `Number.NaN`（它会静默传播） */
function numeric(fn: (n: number) => number) {
  return (value: unknown): number | null => {
    const n = num(value)
    return n === null ? null : fn(n)
  }
}

/**
 * 把参数摊平：既支持 `min(1, 2, 3)`，也支持 `min([1, 2, 3])`。
 *
 * ⚠️ 后者是必须的：expr 里**不能写** `helpers.min.apply(null, list)` ——
 * `.apply` 不是「根上的直接成员」，会被沙箱规则挡掉。
 * 而列表在变参行里天然就是数组，所以「传一个数组」得能work。
 */
function spread(values: unknown[]): unknown[] {
  return values.length === 1 && Array.isArray(values[0]) ? (values[0] as unknown[]) : values
}

function min(...values: unknown[]): number | null {
  const nums = spread(values)
    .map((v) => num(v))
    .filter((v): v is number => v !== null)
  return nums.length === 0 ? null : Math.min(...nums)
}

function max(...values: unknown[]): number | null {
  const nums = spread(values)
    .map((v) => num(v))
    .filter((v): v is number => v !== null)
  return nums.length === 0 ? null : Math.max(...nums)
}

/* ══════════════════════════════════════════════════════════════
   颜色 —— 解析与格式化
   ══════════════════════════════════════════════════════════════ */

/**
 * 把颜色字面量解析成 `[r, g, b, a]`（0–255 整数 + 0–1 的 alpha）。
 *
 * 支持 `#rgb` / `#rrggbb` / `#rrggbbaa`，以及 `rgb/rgba(...)` 文本。
 * ⚠️ **不支持颜色名**（`red` / `blue`）：那要一张 148 项的表，
 * 而控件给出来的一直是十六进制。解析不出来返回 `null`（不抛）。
 *
 * ⚠️ 8 位十六进制的第 4 组是 **alpha**（`#ffffff80`）——
 * 这是仓库里控件实际用的形状（见 `widgets/NpWidgetColor`），
 * 别按「rrggbb + 多余两位」理解。
 */
function parseColor(value: unknown): [number, number, number, number] | null {
  if (typeof value !== 'string') return null
  const text = value.trim().toLowerCase()

  if (text.startsWith('#')) {
    const hex = text.slice(1)
    const expand = (s: string) => Number.parseInt(s + s, 16)
    if (hex.length === 3 || hex.length === 4) {
      if (!/^[0-9a-f]+$/.test(hex)) return null
      return [expand(hex[0]!), expand(hex[1]!), expand(hex[2]!), hex[3] ? expand(hex[3]!) / 255 : 1]
    }
    if (hex.length === 6 || hex.length === 8) {
      if (!/^[0-9a-f]+$/.test(hex)) return null
      return [
        Number.parseInt(hex.slice(0, 2), 16),
        Number.parseInt(hex.slice(2, 4), 16),
        Number.parseInt(hex.slice(4, 6), 16),
        hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1,
      ]
    }
    return null
  }

  const fn = text.match(/^rgba?\(([^)]+)\)$/)
  if (fn) {
    const parts = fn[1]!.split(/[,\s/]+/).filter((p) => p !== '')
    if (parts.length < 3) return null
    const channel = (raw: string) => {
      const n = Number(raw.replace('%', ''))
      if (!Number.isFinite(n)) return null
      const scaled = raw.includes('%') ? (n / 100) * 255 : n
      return Math.max(0, Math.min(255, Math.round(scaled)))
    }
    const r = channel(parts[0]!)
    const g = channel(parts[1]!)
    const b = channel(parts[2]!)
    if (r === null || g === null || b === null) return null
    const a = parts[3] === undefined ? 1 : Math.max(0, Math.min(1, Number(parts[3])))
    return [r, g, b, Number.isFinite(a) ? a : 1]
  }

  return null
}

const hex2 = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')

/** 颜色 → `#rrggbb`（含 alpha 时给 `#rrggbbaa`） */
function toHex(value: unknown): string | null {
  const c = parseColor(value)
  if (!c) return null
  const base = `#${hex2(c[0])}${hex2(c[1])}${hex2(c[2])}`
  return c[3] >= 1 ? base : `${base}${hex2(c[3] * 255)}`
}

/** 颜色 → `[r, g, b]`（0–255 整数；拼字符串用 `join`） */
function toRgb(value: unknown): number[] | null {
  const c = parseColor(value)
  return c ? [c[0], c[1], c[2]] : null
}

/** 颜色 → `[r, g, b, a]`（a 是 0–255 整数，和 CSS 的 rgba() 一致） */
function toRgba(value: unknown): number[] | null {
  const c = parseColor(value)
  return c ? [c[0], c[1], c[2], Math.round(c[3] * 255)] : null
}

/** 颜色 → 整数 `0xRRGGBB`（指令里常见这种写法） */
function colorToNumber(value: unknown): number | null {
  const c = parseColor(value)
  return c ? (c[0] << 16) | (c[1] << 8) | c[2] : null
}

/** `[r, g, b]` → 颜色（`toRgb` 的反向） */
function fromRgb(value: unknown): string | null {
  if (!Array.isArray(value) || value.length < 3) return null
  const n = (i: number) => {
    const v = num(value[i])
    return v === null ? null : Math.round(v)
  }
  const r = n(0)
  const g = n(1)
  const b = n(2)
  if (r === null || g === null || b === null) return null
  return `#${hex2(r)}${hex2(g)}${hex2(b)}`
}

/** 整数 `0xRRGGBB` → 颜色（`colorToNumber` 的反向） */
function fromColorNumber(value: unknown): string | null {
  const n = num(value)
  if (n === null) return null
  const int = Math.max(0, Math.min(0xffffff, Math.trunc(n)))
  return `#${hex2(int >> 16)}${hex2((int >> 8) & 0xff)}${hex2(int & 0xff)}`
}

/* ══════════════════════════════════════════════════════════════
   时间
   ══════════════════════════════════════════════════════════════ */

/**
 * 时间（epoch 毫秒或时间文本）→ epoch 毫秒；解析不出来给 null。
 *
 * ⚠️ 走内核唯一那份 `parseTime`：`Date.parse` 对「月/日没补零」的写法
 * 会当**本地时间**（`2024-1-2` ≠ `2024-01-02`，在 UTC+8 差一天）。
 * 细节与「刻意不做的事」写在 `node/timeText.ts` 顶部。
 */
function toEpoch(value: unknown): number | null {
  return parseTime(value)
}

export type TimePart = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'weekday'

/**
 * 取时间的某一部分。
 *
 * ⚠️ `zone` 必须**显式给**，因为这件事有真实的后果：
 *   · `'local'` —— 用户看到的日期。**但它依赖运行环境的时区**，
 *     同一份文档在不同机器上可能算出不同的「日」；
 *   · `'utc'` —— 到处一样，但可能比用户以为的差一天。
 * 让调用方（节点上的枚举）明确选，而不是我们替他默认 ——
 * 运行服务应该固定自己的时区（`TZ`），这件事要在文档里说清。
 *
 * 月份是 **1–12**（不是 JS 的 0–11）—— 输出给人看的东西不搞零基。
 * 星期天是 **0**（跟 JS 一致，周日=0…周六=6）。
 */
function timePart(value: unknown, part: unknown, zone: unknown = 'local'): number | null {
  const ms = toEpoch(value)
  if (ms === null) return null
  const date = new Date(ms)
  const utc = str(zone) === 'utc'
  const key = str(part)
  switch (key as TimePart) {
    case 'year':
      return utc ? date.getUTCFullYear() : date.getFullYear()
    case 'month':
      return (utc ? date.getUTCMonth() : date.getMonth()) + 1
    case 'day':
      return utc ? date.getUTCDate() : date.getDate()
    case 'hour':
      return utc ? date.getUTCHours() : date.getHours()
    case 'minute':
      return utc ? date.getUTCMinutes() : date.getMinutes()
    case 'second':
      return utc ? date.getUTCSeconds() : date.getSeconds()
    case 'weekday':
      return utc ? date.getUTCDay() : date.getDay()
    default:
      throw new Error(`timePart 不认识的部分「${key}」`)
  }
}

/* ══════════════════════════════════════════════════════════════
   文本
   ══════════════════════════════════════════════════════════════ */

/** 第 N 个字符（负数从末尾数）；越界给空串 */
function at(value: unknown, index: unknown): string {
  const text = str(value)
  const i = Math.trunc(num(index) ?? 0)
  const real = i < 0 ? text.length + i : i
  return text[real] ?? ''
}

function replace(value: unknown, from: unknown, to: unknown): string {
  const needle = str(from)
  if (needle === '') return str(value)
  /* ⚠️ 用 split/join 而不是 replace：`replace` 只换第一个，
   * 而「把所有占位符换掉」才是这里的语义 */
  return str(value).split(needle).join(str(to))
}

function repeat(value: unknown, times: unknown): string {
  const n = Math.max(0, Math.min(1000, Math.trunc(num(times) ?? 0)))
  return str(value).repeat(n)
}

/**
 * 按模板拼字符串：`format('单号 {0}，{1} 件', [no, qty])`。
 *
 * `{n}` 按下标取参数；取不到就换成空串（不是留个 `{3}` 在纸上）。
 * ⚠️ 和模板字符串（`` `…${…}` ``）的分工：那个在**写表达式时**用，
 * 这个在**运行时**用（模板文本来自数据/控件时只能用它）。
 */
function format(pattern: unknown, args: unknown): string {
  const list = Array.isArray(args) ? args : [args]
  return str(pattern).replace(/\{(\d+)\}/g, (_, index: string) => str(list[Number(index)]))
}

/** 时间戳/ISO → ISO 文本（UTC，带 Z） */
function toIso(value: unknown): string | null {
  const ms = toEpoch(value)
  return ms === null ? null : new Date(ms).toISOString()
}

/* ══════════════════════════════════════════════════════════════
   列表
   ══════════════════════════════════════════════════════════════ */

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

function first(value: unknown): unknown {
  const list = asList(value)
  return list.length > 0 ? list[0] : null
}

function last(value: unknown): unknown {
  const list = asList(value)
  return list.length > 0 ? list[list.length - 1] : null
}

function reverse(value: unknown): unknown[] {
  return [...asList(value)].reverse()
}

/** 去重：按**字符串形式**比（`1` 和 `"1"` 算同一个）—— 和 contains 一致 */
function unique(value: unknown): unknown[] {
  const seen = new Set<string>()
  const out: unknown[] = []
  for (const item of asList(value)) {
    const key = str(item)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}

/** 求和：转不成数字的项**跳过**（不把整列变成 null） */
function sum(value: unknown): number {
  return asList(value).reduce((total: number, item) => total + (num(item) ?? 0), 0)
}

/** 平均；空列表给 null（不是 0 —— 「没有数据」和「平均值是 0」不是一回事） */
function average(value: unknown): number | null {
  const list = asList(value).filter((item) => num(item) !== null)
  if (list.length === 0) return null
  return list.reduce((total: number, item) => total + (num(item) ?? 0), 0) / list.length
}

/** 按字段排序（`field` 空 = 就比元素自己）；数字优先，和比较语义一致 */
function sortBy(value: unknown, field: unknown = '', desc: unknown = false): unknown[] {
  const list = [...asList(value)]
  const path = str(field)
  const sign = bool(desc) ? -1 : 1
  list.sort((a, b) => {
    const x = valueAt(a, path)
    const y = valueAt(b, path)
    const nx = num(x)
    const ny = num(y)
    if (nx !== null && ny !== null) return (nx - ny) * sign
    const sx = str(x)
    const sy = str(y)
    return (sx === sy ? 0 : sx < sy ? -1 : 1) * sign
  })
  return list
}

/** 按字段取出一列（`map` 的声明式版本） */
function pluck(value: unknown, field: unknown): unknown[] {
  const path = str(field)
  return asList(value).map((item) => valueAt(item, path))
}

/**
 * 有没有一个为真 / 是不是全为真。
 *
 * ⚠️ 这两个（以及 `len` / `join` / `contains`）是 expr 里**唯一**能做的
 * 「遍历」—— 因为 expr 没有函数，写不出 `list.some(x => …)`。
 * 这不是缺陷，是设计：**高阶操作用声明式 helper 表达**，
 * 而不是把函数传进来（那就等于放开了调用任意值）。
 *
 * ## ★★ 空槽**不参与**判定（`boolean.and` 为此错了很久）
 *
 * 它们的入参通常来自 `api.repeat(...)`，而变参行**末尾永远多一个
 * 「待接的空槽」**（`repeaterCountFor` = `lastConnected + 2`）——
 * 那个位置是 `undefined`。
 *
 * 原来直接 `every(item => bool(item))`，而 `bool(undefined)` 是 `false`，
 * 于是**只要还有一个空槽没接，`boolean.and` 就永远是假**：
 * 连了两个真值、第三个槽空着，输出 `false`。
 * 实测：`all([true, true, undefined])` 曾经是 `false`。
 *
 * 现在的规矩：**先把空槽滤掉，再判**。空槽是「还没接的地方」，
 * 不是「用户给了一个假值」—— `any` 一直是对的（`undefined` 恰好在
 * `some` 里就是假），`all` 是漏掉这一层才错的。两个现在对称了。
 *
 * ⚠️ 滤空之后 `all([])` 是 `true`、`any([])` 是 `false` ——
 * 数学上分别是「空合取为真」「空析取为假」，也是 JS 的 `every` / `some`
 * 对空数组的默认行为。
 */
function slotsOf(list: unknown): unknown[] {
  const values = Array.isArray(list) ? list : [list]
  /* 只滤 `undefined`（「没接」），**不滤** `null` / `0` / `''`
     —— 那些是用户真的给了值，判定该按 `bool` 走。 */
  return values.filter((item) => item !== undefined)
}

function any(list: unknown): boolean {
  return slotsOf(list).some((item) => bool(item))
}

function all(list: unknown): boolean {
  return slotsOf(list).every((item) => bool(item))
}

/**
 * **按字段 + 操作符过滤列表** —— expr 里的「高阶操作」长这样。
 *
 * ```ts
 * helpers.filterBy(api.input('items'), 'qty', 'gt', 5)
 * ```
 *
 * ⚠️ 为什么是声明式的（而不是 `list.filter(x => …)`）：
 * expr **没有函数**，写不出「传一个谓词进去」。而放开函数就等于放开
 * 「调用任意值」，前面那些沙箱规则全白做。所以高阶操作一律走
 * **声明式的 helper**：把「怎么筛」表达成数据（字段 + 操作符 + 值）。
 *
 * 操作符就是契约里那 10 种（`docs/type-contract.md` §7），
 * 和 `list.filter` 节点上的枚举控件是同一份。
 *
 * ⚠️ 未知操作符**抛错**（而不是返回空列表）：那是包作者的拼写错误，
 * 静默给个空结果会让人以为「筛完就是没数据」。
 * 这是「helper 不抛异常」那条规矩的**第二个例外**（第一个是 `get` 的路径）。
 */
function filterBy(
  list: unknown,
  field: unknown,
  op: unknown,
  value: unknown = undefined,
): unknown[] {
  if (!Array.isArray(list)) return []
  const operator = str(op) as CompareOp
  if (!COMPARE_OPS.includes(operator)) {
    throw new Error(`filterBy 不认识操作符「${str(op)}」`)
  }
  const path = str(field)
  return list.filter((item) => compare(valueAt(item, path), operator, value))
}

/**
 * 按下标挑一个（`switch` 节点的实现）。
 *
 * 越界 / 负数 → `null`，不抛 —— 下标是上游算出来的，越界是**数据**问题，
 * 不是作者的错。
 */
function pick(index: unknown, values: unknown): unknown {
  if (!Array.isArray(values)) return null
  const i = Math.trunc(num(index) ?? 0)
  if (i < 0 || i >= values.length) return null
  return values[i]
}

/**
 * 安全取路径：`get(data, 'order.items.0.name')`。
 *
 * ⚠️ 这是 `?.` 的替代品（expr 故意不支持可选链）。它**不抛**，
 * 中间任何一段缺失都返回 `null` —— 数据源少一个字段是常态，
 * 不该把整张图变成一条错误诊断。
 */
function get(value: unknown, path: unknown): unknown {
  const keys = str(path)
    .split('.')
    .map((key) => key.trim())
    .filter((key) => key !== '')
  if (keys.length === 0) return value

  let current: unknown = value
  for (const key of keys) {
    if (current === null || current === undefined) return null
    if (typeof current !== 'object' && !Array.isArray(current)) return null
    current = (current as Record<string, unknown>)[key]
  }
  return current === undefined ? null : current
}

/**
 * helper 表 —— **唯一**的可调用集合（`whitelist` 和 `parse` 都从这里读）。
 *
 * 顺序就是文档里的顺序，方便对着看。
 */
export const HELPERS = {
  /* 转换 */
  num,
  str,
  bool,
  /* 长度与空值 */
  len,
  isEmpty,
  /* 字符串 */
  upper: (value: unknown) => str(value).toUpperCase(),
  lower: (value: unknown) => str(value).toLowerCase(),
  trim: (value: unknown) => str(value).trim(),
  pad,
  fixed,
  join,
  contains,
  slice,
  /* 数值 */
  round,
  floor: numeric(Math.floor),
  ceil: numeric(Math.ceil),
  abs: numeric(Math.abs),
  min,
  max,
  pow: (a: unknown, b: unknown) => {
    const base = num(a)
    const exponent = num(b)
    return base === null || exponent === null ? null : base ** exponent
  },
  /* 转换 */
  toList,
  /* 逻辑 */
  compare: compareWith,
  any,
  all,
  /* 颜色 */
  toHex,
  toRgb,
  toRgba,
  colorToNumber,
  fromRgb,
  fromColorNumber,
  /* 时间 */
  toEpoch,
  toIso,
  timePart,
  /* 文本补充 */
  at,
  replace,
  repeat,
  format,
  startsWith: (value: unknown, prefix: unknown) => str(value).startsWith(str(prefix)),
  endsWith: (value: unknown, suffix: unknown) => str(value).endsWith(str(suffix)),
  /* ⚠️ 空串分隔符 = 按**字符**切（和 JS 的 `split('')` 一致），不是「不切」 */
  split: (value: unknown, separator: unknown) => {
    const text = str(value)
    if (text === '') return []
    const sep = str(separator)
    return sep === '' ? [...text] : text.split(sep)
  },
  /* 数值补充 */
  clamp,
  mod,
  sqrt: numeric(Math.sqrt),
  /* 列表 */
  first,
  last,
  reverse,
  unique,
  sum,
  average,
  sortBy,
  pluck,
  /* 数据 */
  get,
  /* 高阶（声明式）—— expr 没有函数，遍历只能这么表达 */
  filterBy,
  pick,
} as const

export type HelperName = keyof typeof HELPERS

export const HELPER_NAMES = new Set<string>(Object.keys(HELPERS))

/** 给文档/报错用的一句话说明 */
export const HELPER_DOC: Record<HelperName, string> = {
  num: '转数值；转不出来给 null',
  str: '转字符串；null/undefined 给空串',
  bool: '转布尔；空串/空数组/0 为假',
  len: '长度（字符串 / 数组）',
  isEmpty: '是不是空的（0 和 false 不算空）',
  upper: '转大写',
  lower: '转小写',
  trim: '去首尾空白',
  pad: '补位（默认左补 0）：pad(7, 4) → "0007"',
  fixed: '定点小数：fixed(1.5, 2) → "1.50"',
  join: '连接数组：join(list, "-")',
  contains: '包含（字符串或数组）',
  slice: '截取（字符串或数组）',
  round: '四舍五入到 N 位',
  floor: '向下取整',
  ceil: '向上取整',
  abs: '绝对值',
  min: '最小值（忽略转不出来的；也可传一个数组）',
  max: '最大值（忽略转不出来的）',
  pow: '幂',
  toList: '转成列表（单个值包成一项）',
  compare: '一次比较：compare(a, "gt", b)',
  any: '有一个为真就为真：any(api.repeat("args"))',
  all: '全部为真才为真',
  get: '安全取路径：get(data, "order.name")',
  toHex: '颜色 → #rrggbb（带透明度时 #rrggbbaa）',
  toRgb: '颜色 → [r, g, b]（0–255）',
  toRgba: '颜色 → [r, g, b, a]（0–255）',
  colorToNumber: '颜色 → 整数 0xRRGGBB',
  fromRgb: '[r, g, b] → 颜色',
  fromColorNumber: '整数 0xRRGGBB → 颜色',
  toEpoch: '时间 → epoch 毫秒',
  toIso: '时间 → ISO 文本（UTC）',
  timePart: '取时间的某一部分：timePart(t, "day", "local")',
  at: '第 N 个字符（负数从末尾数）',
  replace: '替换**所有**出现的子串',
  repeat: '重复 N 次',
  format: '模板拼接：format("单号 {0}", [no])',
  startsWith: '以…开头',
  endsWith: '以…结尾',
  split: '切成数组',
  clamp: '夹到区间里',
  mod: '取模（结果跟随除数符号）',
  sqrt: '平方根',
  first: '列表第一项',
  last: '列表最后一项',
  reverse: '颠倒',
  unique: '去重',
  sum: '求和（转不成数字的跳过）',
  average: '平均（空列表给 null）',
  sortBy: '按字段排序：sortBy(list, "qty", true)',
  pluck: '取出一列：pluck(list, "qty")',
  filterBy: '按字段过滤列表：filterBy(list, "qty", "gt", 5)',
  pick: '按下标挑一个：pick(1, list)',
}
